import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { clearSession, storage } from "@/api/client";
import * as endpoints from "@/api/endpoints";
import type { Organization, Role } from "@/types";

type AuthState = {
  email: string | null;
  organizations: Organization[];
  activeOrgId: string | null;
  activeOrg: Organization | null;
  role: Role | null;
  isSignedIn: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  switchOrg: (orgId: string) => void;
  refreshOrganizations: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

function readOrganizations(): Organization[] {
  try {
    const raw = localStorage.getItem(storage.organizations);
    return raw ? (JSON.parse(raw) as Organization[]) : [];
  } catch {
    return [];
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [email, setEmail] = useState(() => localStorage.getItem(storage.email));
  const [organizations, setOrganizations] = useState<Organization[]>(readOrganizations);
  const [activeOrgId, setActiveOrgId] = useState(() =>
    localStorage.getItem(storage.activeOrgId)
  );

  const persist = useCallback((orgs: Organization[], active: string | null) => {
    localStorage.setItem(storage.organizations, JSON.stringify(orgs));
    if (active) localStorage.setItem(storage.activeOrgId, active);
    else localStorage.removeItem(storage.activeOrgId);
    setOrganizations(orgs);
    setActiveOrgId(active);
  }, []);

  const signIn = useCallback(
    async (nextEmail: string, password: string) => {
      const session = await endpoints.login(nextEmail, password);
      localStorage.setItem(storage.accessToken, session.accessToken);
      localStorage.setItem(storage.refreshToken, session.refreshToken);
      localStorage.setItem(storage.email, session.user.email);
      setEmail(session.user.email);
      persist(session.organizations, session.organizations[0]?.orgId ?? null);
    },
    [persist]
  );

  const signOut = useCallback(async () => {
    const refreshToken = localStorage.getItem(storage.refreshToken);
    if (refreshToken) {
      // Best effort: the local session goes either way, and a failed call
      // must not strand the user on a page they can no longer use.
      await endpoints.logout(refreshToken).catch(() => undefined);
    }
    clearSession();
    setEmail(null);
    setOrganizations([]);
    setActiveOrgId(null);
  }, []);

  const switchOrg = useCallback(
    (orgId: string) => {
      persist(organizations, orgId);
    },
    [organizations, persist]
  );

  const refreshOrganizations = useCallback(async () => {
    const orgs = await endpoints.listOrganizations();
    const stillValid = orgs.some((org) => org.orgId === activeOrgId);
    persist(orgs, stillValid ? activeOrgId : (orgs[0]?.orgId ?? null));
  }, [activeOrgId, persist]);

  const value = useMemo<AuthState>(() => {
    const activeOrg = organizations.find((org) => org.orgId === activeOrgId) ?? null;
    return {
      email,
      organizations,
      activeOrgId,
      activeOrg,
      role: activeOrg?.role ?? null,
      isSignedIn: Boolean(email && localStorage.getItem(storage.accessToken)),
      signIn,
      signOut,
      switchOrg,
      refreshOrganizations,
    };
  }, [email, organizations, activeOrgId, signIn, signOut, switchOrg, refreshOrganizations]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
